import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Table,
  TablePagination,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import PerfectScrollbarTable from 'src/components/PerfectScrollbarTable';
import TableRefresh from 'src/components/TableRefresh';
import THeadResults from 'src/views/management/ScheduledMessagesView/Results/THeadResults';
import TBodyResults from 'src/views/management/ScheduledMessagesView/Results/TBodyResults';
import BulkOperations from 'src/views/management/ScheduledMessagesView/BulkOperations';
import {
  getScheduledMessages,
  removeScheduledMessages,
  setScheduledMessagesQuery,
  setSelectedScheduledMessages,
} from 'src/actions/scheduledMessagesActions';
import helpers from 'src/helpers';
import { MESSAGES } from 'src/constants';

import resultsStyle from 'src/views/management/ScheduledMessagesView/Results/results.style';

const useStyles = makeStyles()(resultsStyle);

function Results({
  className,
  onChangeSortBy,
  handleRowClick,
  setCopyValuesToBack,
  query = {},
  scheduledMessages = [],
  selectedScheduledMessages = [],
  userRolePermissions = [],
  loading = false,
}) {
  const {
    classes,
    cx,
  } = useStyles();
  const dispatch = useDispatch();

  const [userClickedToRefreshButton, setUserClickedToRefreshButton] = useState(false);

  const { user, azAccountName, databaseBeingUsed } = useSelector((state) => state?.account || {});
  const { id: userId } = user || {};
  const {
    status,
    page,
    pageSize,
    count,
  } = query || {};

  const isViewingSent = status === 'sent';

  const dispatchSetSelectedScheduledMessages = (values) => {
    dispatch(setSelectedScheduledMessages(values));
  };

  const onClickRemoveMessages = (stopSending = false) => {
    const doWhenTry = () => {
      dispatch(getScheduledMessages());
      dispatchSetSelectedScheduledMessages([]);
    };
    const doWhenCatch = () => {
    };

    dispatch(removeScheduledMessages(selectedScheduledMessages, doWhenTry, doWhenCatch, { stopSending }));
  };

  const handlePageChange = (event, newPage) => {
    dispatch(setScheduledMessagesQuery({ page: helpers.transform.toInt10(newPage) + 1 }));
    dispatch(getScheduledMessages());
  };

  const handleSelectOneScheduledMessage = (event, messageId, messageType) => {
    if (isViewingSent) return; // Don't allow selection for sent messages

    const isSelected = selectedScheduledMessages.some(
      (item) => item.id === messageId && item.type === messageType
    );

    if (!isSelected) {
      dispatchSetSelectedScheduledMessages([...selectedScheduledMessages, { id: messageId, type: messageType }]);
      return;
    }
    dispatchSetSelectedScheduledMessages(
      selectedScheduledMessages.filter((item) => item.id !== messageId || item.type !== messageType)
    );
  };

  const handleLimitChange = (event) => {
    dispatch(setScheduledMessagesQuery({
      pageSize: helpers.transform.toInt10(event.target.value),
      page: 1,
    }));
    dispatch(getScheduledMessages());
  };

  const handleRefreshPage = () => {
    setUserClickedToRefreshButton(true);
    dispatch(getScheduledMessages());
  };

  useEffect(() => {
    if (userClickedToRefreshButton && !loading) {
      setUserClickedToRefreshButton(false);
      if (page > 1 && !scheduledMessages?.length) {
        dispatch(setScheduledMessagesQuery({
          page: 1,
        }));
        dispatch(getScheduledMessages());
      }
    }
  }, [loading]);

  const statusText = isViewingSent ? 'Sent' : 'Scheduled';
  const connectionText = count === 1 ? 'Connection' : 'Connections';

  // Handle count = -1 (indicates "many" for sent status with >5000 items)
  const getCountText = () => {
    if (count === -1) {
      return `5,000+ ${statusText} ${connectionText} found. Page ${page}`;
    }
    if (count) {
      return `${count.toLocaleString()} ${statusText} ${connectionText} found. Page ${page} of ${Math.ceil(count / pageSize)}`;
    }
    return `${statusText} Connections list is empty`;
  };

  return (
    <>
      <Typography
        color="textSecondary"
        variant="body2"
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1, // Add space between text and button
        }}
      >
        <span>
          {getCountText()}
        </span>
        <TableRefresh
          onRefresh={handleRefreshPage}
          refreshRateLimitMs={MESSAGES.RATE_LIMIT_REFRESH_SCHEDULED_MESSAGES}
          loading={loading}
          tooltipText="Refresh scheduled connections"
        />
      </Typography>
      <Card className={cx(classes.root, className)}>
        <PerfectScrollbarTable subDefaultHeight={200}>
          <Box>
            <Table stickyHeader className={classes.table}>
              <THeadResults
                classes={classes}
                query={query}
                scheduledMessages={scheduledMessages}
                selectedScheduledMessages={selectedScheduledMessages}
                onChangeSortBy={onChangeSortBy}
                setSelectedScheduledMessages={dispatchSetSelectedScheduledMessages}
              />
              <TBodyResults
                classes={classes}
                scheduledMessages={scheduledMessages}
                selectedScheduledMessages={selectedScheduledMessages}
                userId={userId}
                azAccountName={azAccountName}
                databaseBeingUsed={databaseBeingUsed}
                handleSelectOneScheduledMessage={handleSelectOneScheduledMessage}
                handleRowClick={handleRowClick}
                handleRefreshPage={handleRefreshPage}
                setCopyValuesToBack={setCopyValuesToBack}
                query={query}
              />
            </Table>
          </Box>
        </PerfectScrollbarTable>
        <TablePagination
          component="div"
          count={count}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={page - 1}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
      {!isViewingSent && (
        <BulkOperations
          open={!helpers.isEmpty(selectedScheduledMessages) && selectedScheduledMessages.length > 0}
          selected={selectedScheduledMessages}
          scheduledMessages={scheduledMessages}
          onClickRemoveMessages={onClickRemoveMessages}
          userRolePermissions={userRolePermissions}
        />
      )}
    </>
  );
}

Results.propTypes = {
  onChangeSortBy: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  setCopyValuesToBack: PropTypes.func.isRequired,
  className: PropTypes.string,
  scheduledMessages: PropTypes.array,
  selectedScheduledMessages: PropTypes.array,
  userRolePermissions: PropTypes.array,
  query: PropTypes.object,
  loading: PropTypes.bool,
};

Results.defaultProps = {
  className: '',
  query: {},
  scheduledMessages: [],
  selectedScheduledMessages: [],
  userRolePermissions: [],
  loading: false,
};

export default Results;
